import React, { useEffect, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import DownloadApp from "components/cta/DownloadApp.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import { useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { useVisitors } from "hooks/useVisitors";
import { ReactComponent as ShareIcon } from "feather-icons/dist/icons/share-2.svg";
import { ReactComponent as CopyIcon } from "feather-icons/dist/icons/copy.svg";
import { NotificationContainer, NotificationManager } from "react-notifications";
import LoadingSpinner from "components/misc/LoadingSpinner.js";

// ── Layout ──────────────────────────────────────────────────────────────────
const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;
const HighlightedTextInverse = tw.span`bg-gray-100 text-primary-500 px-4 transform -skew-x-12 inline-block`;

const TwoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 1100px;
  margin: 3rem auto;
  padding: 0 1.5rem;
  gap: 3rem;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const TextColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const QrColumn = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Heading = styled.h1`
  font-size: 2.2rem;
  font-weight: 800;
  color: #1a202c;
  line-height: 1.2;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  font-size: 1.05rem;
  color: #4a5568;
  line-height: 1.7;
  margin-bottom: 1.75rem;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const WaShareButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #25D366;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
  &:hover { background: #1ebe5d; color: #fff; }
`;

const CopyLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #edf2f7;
  color: #2d3748;
  border: 1px solid #cbd5e0;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #e2e8f0; }
`;

const QrWrapper = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.10);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
`;

const QrCaption = styled.p`
  font-size: 0.8rem;
  color: #718096;
  text-align: center;
  max-width: 220px;
`;

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(255, 255, 255, 0.75);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ── Component ────────────────────────────────────────────────────────────────
export default () => {
  const { guid } = useParams();
  const appBaseUrl = process.env.REACT_APP_BASE_URL;
  const [isGettingVisitor, setIsGettingVisitor] = useState(true);
  const { getVisitorByGuid, guidVisitor, loading } = useVisitors();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!guid) return;
    getVisitorByGuid(guid)
      .catch(() => {})
      .finally(() => setIsGettingVisitor(false));
  }, [guid]); // eslint-disable-line react-hooks/exhaustive-deps

  const invitationUrl = `${appBaseUrl}invitacion/${guid}`;
  const visitorName = guidVisitor?.visitorName ?? "";
  const visitorPhone = guidVisitor?.visitorPhoneNumber ?? "";
  const waMessage = encodeURIComponent(
    `¡Hola ${visitorName}! Aquí está tu link de acceso personal al fraccionamiento (NO compartir): ${invitationUrl}`
  );
  const waLink = `https://wa.me/${visitorPhone}?text=${waMessage}`;

  const copyLink = () => {
    navigator.clipboard?.writeText(invitationUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      NotificationManager.warning("No se pudo copiar al portapapeles", "Error", 3000);
    });
  };

  return (
    <>
      {(loading || isGettingVisitor) && (
        <SpinnerOverlay>
          <LoadingSpinner text="Cargando invitación…" />
        </SpinnerOverlay>
      )}
      {!loading && !isGettingVisitor && (
        <AnimationRevealPage>
          <TwoColumn>
            <TextColumn>
              <Heading>
                ¡Invitación lista! Compártela con tu{" "}
                <HighlightedText>Invitado.</HighlightedText>
              </Heading>
              <Description>
                Tu invitación con <strong>QR dinámico anti-fraude</strong> ha sido creada.
                El código es único para <strong>{visitorName || "tu visitante"}</strong>, cambia cada 30 segundos
                y solo puede usarse una vez — nadie más puede acceder aunque lo compartan.
                <br /><br />
                Comparte el enlace por WhatsApp o cópialo para enviarlo por otro medio.
              </Description>
              <ButtonRow>
                <WaShareButton href={waLink} target="_blank" rel="noopener noreferrer">
                  <ShareIcon style={{ width: 16, height: 16 }} />
                  Compartir vía WhatsApp
                </WaShareButton>
                <CopyLinkButton onClick={copyLink}>
                  <CopyIcon style={{ width: 16, height: 16 }} />
                  {copied ? "¡Copiado!" : "Copiar link"}
                </CopyLinkButton>
              </ButtonRow>
            </TextColumn>

            <QrColumn>
              <QrWrapper>
                <QRCode
                  value={invitationUrl}
                  bgColor="#ffffff"
                  fgColor="#1a202c"
                  size={220}
                />
                <QrCaption>
                  Escanea con la cámara del teléfono o el lector a la entrada del fraccionamiento.
                </QrCaption>
              </QrWrapper>
            </QrColumn>
          </TwoColumn>

          <DownloadApp
            text={<>Próximamente espera por nuestra versión <HighlightedTextInverse>Móvil.</HighlightedTextInverse></>}
          />
          <Footer />
        </AnimationRevealPage>
      )}
      <NotificationContainer />
    </>
  );
};
